/// <reference types="vite/client" />
import { type ReactNode, useEffect } from 'react';
import {
  Asset,
  Outlet,
  createRootRouteWithContext,
  Scripts,
  useTags,
} from '@tanstack/react-router';
import { ErrorBoundary } from 'react-error-boundary';
import { QueryClientProvider, type QueryClient } from '@tanstack/react-query';
import { MantineProvider, ColorSchemeScript } from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/notifications/styles.css';
import '~/styles/mantine-overrides.css';
// Shared with the marketing website so the type stack cannot drift between the
// two surfaces — see packages/shared/styles/fonts.css. The .woff2 files it
// references still come from THIS app's public/fonts.
import '@repo/shared/fonts.css';
// Presentation-animation foundation: the global prefers-reduced-motion guard.
// Keyframes themselves live in their consuming modules (Reveal.module.css) — CSS
// Modules scope animation-name, so they can't be shared from here.
import '~/styles/motion.css';
// Reserve the document scrollbar gutter so a scrollbar appearing/disappearing during
// an entrance never shifts centred content sideways (the left/right "jitter") on pages
// that scroll on the document — auth, onboarding, style-guide, parsing-rules.
import '~/styles/scrollbar-gutter.css';
import { APP_NAME, errorTracker } from '@repo/shared/common';
import { theme, cssVariablesResolver } from '~/lib/theme';
import { PageViewTracker } from '~/components/shell/PageViewTracker';
import { RouteProgressBar } from '~/components/shell/RouteProgressBar';
import { PreferencesProvider } from '~/lib/preferences-context';
import { preferencesQueryOptions } from '~/lib/preferences.query';
import '~/lib/i18n';

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  // Resolve the visitor's locale + timezone from the request ONCE (cookie → CF geo →
  // header), server-awaited so it ships in the SSR HTML and the first client render reads
  // the identical value — the framework-correct fix for localized-date/number hydration
  // mismatches (see PreferencesProvider + lib/preferences.ts).
  loader: ({ context }) => context.queryClient.ensureQueryData(preferencesQueryOptions()),
  head: () => ({
    meta: [
      { charSet: 'utf-8' },
      { name: 'viewport', content: 'width=device-width, initial-scale=1' },
      { title: APP_NAME },
    ],
    links: [
      // Montserrat (variable, 100–900) is the body/UI face — one file covers
      // every weight, preloaded to avoid FOUT on first paint.
      { rel: 'preload', href: '/fonts/Montserrat-latin.woff2', as: 'font', type: 'font/woff2', crossOrigin: 'anonymous' },
      // Libre Baskerville (variable, 400–700) carries page titles / greetings
      // (Display + h1/h2) — preload the display face.
      { rel: 'preload', href: '/fonts/LibreBaskerville-latin.woff2', as: 'font', type: 'font/woff2', crossOrigin: 'anonymous' },
      // Cormorant Garamond italic (latin) is the decorative accent — it lands
      // INLINE inside an already-painted Baskerville heading (the "Good morning,
      // <em>Mathew</em>" / "built to <em>last</em>" flourish), so an on-demand
      // load makes the accent word visibly pop in after the rest. Preload it (+
      // font-display: block in fonts.css) so it holds with the heading and paints
      // in one go — no flash. The remaining Cormorant faces (roman, latin-ext) +
      // mono figures stay on-demand (font-display: optional).
      { rel: 'preload', href: '/fonts/CormorantGaramond-Italic-latin.woff2', as: 'font', type: 'font/woff2', crossOrigin: 'anonymous' },
    ],
  }),
  component: RootComponent,
});

function handleBoundaryError(error: unknown, info: { componentStack?: string | null }): void {
  const err = error instanceof Error ? error : new Error(String(error));
  errorTracker.captureException(err, {
    source: 'errorBoundary',
    componentStack: info.componentStack ?? undefined,
  });
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  return String(error);
}

function RootErrorFallback({
  error,
  resetErrorBoundary,
}: {
  error: unknown;
  resetErrorBoundary: () => void;
}): ReactNode {
  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <title>Error — {APP_NAME}</title>
      </head>
      <body>
        <div style={{ padding: '2rem', fontFamily: 'system-ui, sans-serif' }}>
          <h1>Something went wrong</h1>
          <p>{getErrorMessage(error)}</p>
          <button type="button" onClick={resetErrorBoundary}>
            Try again
          </button>
        </div>
      </body>
    </html>
  );
}

function GlobalErrorHandlers(): ReactNode {
  useEffect(() => {
    function onError(event: ErrorEvent): void {
      errorTracker.captureException(
        event.error instanceof Error
          ? event.error
          : new Error(event.message),
        { source: 'window.error' },
      );
    }

    function onUnhandledRejection(event: PromiseRejectionEvent): void {
      errorTracker.captureException(
        event.reason instanceof Error
          ? event.reason
          : new Error(String(event.reason)),
        { source: 'unhandledrejection' },
      );
    }

    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onUnhandledRejection);
    return () => {
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onUnhandledRejection);
    };
  }, []);
  return null;
}

function RootComponent(): ReactNode {
  // The QueryClient is created in `getRouter()` and lives on the router context
  // (one per request on the server, one on the client). Reading it here — rather
  // than creating a second client — is what lets loader-prefetched, SSR-hydrated
  // data and component `useQuery` calls share a single cache.
  const { queryClient } = Route.useRouteContext();

  return (
    <ErrorBoundary
      FallbackComponent={RootErrorFallback}
      onError={handleBoundaryError}
    >
      <QueryClientProvider client={queryClient}>
        <PreferencesProvider>
          <MantineProvider
            theme={theme}
            cssVariablesResolver={cssVariablesResolver}
            defaultColorScheme="auto"
          >
            <Notifications position="top-right" />
            <PageViewTracker />
            <RootDocument>
              {/* 0ms navigation progress bar; route errors are handled by the
                  router's defaultErrorComponent (see router.tsx), not a wrapper. */}
              <RouteProgressBar />
              <Outlet />
            </RootDocument>
          </MantineProvider>
        </PreferencesProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

/**
 * Route-managed head tags — the router's `<HeadContent />`, except route STYLESHEETS
 * render as React 19 managed resources (`precedence`) instead of plain `<link>`s.
 *
 * Why: stock `HeadContent` keys each matched route's CSS link to the match, so a
 * client-side navigation REMOVES the outgoing route's stylesheet and re-inserts the
 * incoming route's in the very commit that mounts the destination page — nothing
 * waits for the load. Deployed Workers assets serve `max-age=0, must-revalidate`,
 * so that re-insert costs a network round trip, during which the new page sits
 * unstyled (the Modeling/Portfolio flicker: the segment rail's two-column layout
 * collapsing to a raw stack, then snapping right). `precedence` makes React hoist
 * and dedupe the link by href, keep it mounted across navigations (no removal → no
 * refetch → no unstyled window), and suspend a transition commit on a genuinely new
 * stylesheet until it has loaded. Everything else (meta/title/preloads/scripts)
 * still renders through the router's own `<Asset>`.
 *
 * This is a forward-port of the router's OWN fix: @tanstack/react-router v1.168.22
 * ships identical behavior in stock `HeadContent` ("Preserve shared route stylesheet
 * links across client navigation by rendering route CSS assets with React stylesheet
 * precedence", PR #7186). Once the router is upgraded to >= 1.168.22, delete this
 * component and render `<HeadContent />` again.
 */
function AppHeadContent(): ReactNode {
  const tags = useTags();
  return tags.map((tag) => {
    const key = `tsr-meta-${JSON.stringify(tag)}`;
    if (tag.tag === 'link' && tag.attrs?.['rel'] === 'stylesheet') {
      return <link key={key} {...tag.attrs} precedence="default" />;
    }
    return <Asset {...tag} key={key} />;
  });
}

function RootDocument({ children }: Readonly<{ children: ReactNode }>): ReactNode {
  return (
    <html lang="en" suppressHydrationWarning>
      <head>
        <ColorSchemeScript defaultColorScheme="auto" />
        <AppHeadContent />
      </head>
      <body>
        <GlobalErrorHandlers />
        {children}
        <Scripts />
      </body>
    </html>
  );
}
